/**
 * BlockedReasonTip — wraps a control that is disabled for a reason, and surfaces
 * that reason on hover or click.
 *
 * Renders `children` bare when there is no `message`, so it can wrap a control
 * unconditionally.
 *
 * There are already several hand-copied `StatusPopover` components in this repo
 * doing roughly this. This is not another one: it is the corrected version, and new
 * call sites should use it rather than copying a table's local copy. Three things
 * here are load-bearing, and every one of them was a bug first:
 *
 *  1. **The bail-out is after every hook.** `message` changes at runtime — that is
 *     exactly what a device starting to alarm does — so an early return above the
 *     effects changes the hook count between renders and React kills the subtree
 *     with "Rendered fewer hooks than expected".
 *
 *  2. **The panel is portalled to `document.body`.** A transformed ancestor becomes
 *     the containing block for `position: fixed`, and even the identity matrix a
 *     finished CSS transition settles on counts. Inside a drawer that slides in,
 *     a viewport-clamped `left` resolves from the drawer's edge instead of the
 *     viewport and the panel lands off-screen: right text, right DOM node, wholly
 *     invisible.
 *
 *  3. **The wrapped control must not swallow the pointer.** Chrome fires no mouse
 *     events on a `disabled` form control and does not bubble them, so a hover
 *     aimed at the control reaches neither it nor this wrapper. The caller's CSS
 *     has to take the disabled control out of hit-testing
 *     (`pointer-events: none`), or nothing here ever fires. Check it with
 *     `document.elementFromPoint` at the control's centre: it must return this
 *     wrapper, not the control.
 *
 * Click pins it open. On a tablet there is no hover at all, so without that the
 * reason is simply unreachable — and tablets are what the floor uses.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PANEL_W = 260;

/* `block`: wrap as a full-width block instead of inline-block. Menu items and table
   rows are full-width flex children, and an inline-block wrapper collapses them to
   their content width. */
const BlockedReasonTip = ({ children, message, block = false }) => {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  const open = hovering || pinned;

  const place = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - PANEL_W / 2, window.innerWidth - PANEL_W - 8));

    setStyle({
      position: 'fixed',
      /* Above the app's --z-modal (500), so this clears a drawer. */
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${PANEL_W}px`,
      whiteSpace: 'normal',
    });
  };

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    place();
    setHovering(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setHovering(false), 100);
  };

  const handleClick = () => {
    clearTimeout(hoverTimeout.current);
    place();
    setPinned((p) => !p);
    setHovering(false);
  };

  /* Position is measured once on open, so close on scroll rather than tracking —
     the message is one sentence, not something to read while scrolling. */
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => { setHovering(false); setPinned(false); };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setPinned(false); };
    const onKey = (e) => { if (e.key === 'Escape') setPinned(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  /* See (1) above — after the hooks, never before. */
  if (!message) return children;

  return (
    <span
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      style={{
        position: 'relative',
        display: block ? 'block' : 'inline-block',
        width: block ? '100%' : undefined,
        cursor: 'not-allowed',
      }}
    >
      {children}
      {open
        ? createPortal(
          <span className="na-type-popover" style={style}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
              {message}
            </span>
          </span>,
          document.body,
        )
        : null}
    </span>
  );
};

export default BlockedReasonTip;
